import {
  CreditCard, Plus, Pencil, Trash2, Search, Eye,
  Image, Mail, MapPin, Phone, User,
  Briefcase, Building2, Layers, Monitor, FileText,
  Sparkles, Award, Package, TrendingUp, HeartHandshake, Target, Megaphone
} from 'lucide-react';
import type {
  DisplayFields, ContactInfo, GovServiceInfo, CompanyIntro, CompanyQualifications,
  Cooperation, CompanyPromo, CardConfig, UnitProfile, UnitDepartmentItem,
  SupplementInfo, SocialMediaAccount
} from './types';

// ============ 默认电子名片背景图 ============
export const DEFAULT_BACKGROUND_LIST = [
   'https://www.cdkmzs.com/uploads/platform/0703/card_bg.png',
    'https://www.cdkmzs.com/uploads/platform/0703/card_bg2.png',
    'https://www.cdkmzs.com/uploads/platform/0703/card_bg3.png',
];

// ============ 机构称谓（企业与政务单位共用同一套页面） ============
/**
 * 按机构类型返回统一称谓：orgType=1 为「企业」，其余（政府/单位等）为「单位」；
 * orgType 缺失时按 tenantType 兜底
 */
export const getOrgLabel = (orgType?: number | string | null, tenantType?: number | string | null): string => {
  const enterprise = orgType !== undefined && orgType !== null && orgType !== ''
    ? Number(orgType) === 1
    : Number(tenantType) === 1;
  return enterprise ? '企业' : '单位';
};

// ============ 页面内容选项（只保留企业简介、核心业务、企业愿景、企业理念） ============
export const pageContentOptions = [
  { value: 'companyIntro', label: '企业简介', labelEn: 'COMPANY PROFILE', icon: FileText },
  { value: 'unitIntro', label: '单位简介', labelEn: 'UNIT PROFILE', icon: FileText },
  { value: 'coreBusiness', label: '核心业务', labelEn: 'CORE BUSINESS', icon: Briefcase },
  { value: 'companyVision', label: '企业愿景', labelEn: 'OUR VISION', icon: Target },
  { value: 'companyPhilosophy', label: '企业理念', labelEn: 'CORPORATE PHILOSOPHY', icon: Sparkles },
  { value: 'productIntro', label: '产品介绍', labelEn: 'PRODUCT INTRODUCTION', icon: Package },
  // 特色服务与重点工作（可见范围与产品介绍一致）
  { value: 'featuredBiz', label: '特色服务与重点工作', labelEn: 'FEATURED SERVICES', icon: HeartHandshake },
  { value: 'companyPromo', label: '企业宣传', labelEn: 'COMPANY PROMO', icon: Megaphone },
];

/** 页面内容选项文案按机构称谓适配：企业愿景/企业理念/企业宣传 → 单位愿景/单位理念/单位宣传 */
export const getPageContentOptions = (orgType?: number | string | null, tenantType?: number | string | null) => {
  const orgLabel = getOrgLabel(orgType, tenantType);
  return pageContentOptions.map(o => ({
    ...o,
    label: o.label.startsWith('企业') ? `${orgLabel}${o.label.slice(2)}` : o.label,
  }));
};

// ============ 电子名片展示字段选项（去掉头像） ============
export const displayFieldOptions = [
  { value: 'name', label: '姓名', icon: User },
  { value: 'position', label: '职位', icon: Briefcase },
  { value: 'phone', label: '手机号', icon: Phone },
  { value: 'email', label: '邮箱', icon: Mail },
  { value: 'company', label: '公司名称', icon: Building2 },
  { value: 'companyAddress', label: '公司地址', icon: MapPin },
];

// ============ 名片布局选项 ============
export const cardLayouts = [
  { value: 'left', label: '布局一', desc: '左侧布局', preview: 'left' },
  { value: 'center', label: '布局二', desc: '居中布局', preview: 'center' },
  { value: 'right', label: '布局三', desc: '右侧布局', preview: 'right' },
  { value: 'preview-4', label: '布局四', desc: '预览四布局', preview: 'preview-4' },
];

// ============ 模块类型选项 ============
export const MODULE_TYPE_OPTIONS = [
  { value: '1', label: '模块一' },
  { value: '2', label: '模块二' },
  { value: '3', label: '模块三' },
];

// ============ 企业类型预设选项 ============
export const COMPANY_TYPE_OPTIONS = [
  '国有独资', '总部企业', '上市公司', '民营企业', '外资企业',
  '中外合资', '中外合作', '合伙企业', '个人独资企业', '有限责任公司',
  '股份有限公司', '集团企业', '其他'
];

// ============ 资质分类 ============
export const QUALIFICATION_CATEGORIES = [
  { value: 'honors', label: '企业荣誉', icon: Award },
  { value: 'businessQualifications', label: '经营资质', icon: FileText },
  { value: 'strength', label: '规模实力', icon: Building2 },
  { value: 'endorsement', label: '合作背书', icon: HeartHandshake },
];

// ============ 预设颜色 ============
export const presetColors = [
  '#1c5bff', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#ec4899', '#6366f1', '#84cc16', '#f97316',
  '#64748b', '#334155', '#ffffff', '#000000'
];

// ============ 主题配色预设 ============
export const THEME_COLOR_OPTIONS = [
  { name: '科技蓝', value: '#1DA9FF' },
  { name: '政务蓝', value: '#1641B4' },
  { name: '中国红', value: '#E34D59' },
  { name: '生态绿', value: '#00A870' },
  { name: '活力橙', value: '#ED7B2F' },
  { name: '稳重紫', value: '#8A5CF6' },
];

export const DEFAULT_THEME_COLOR = '#ffffff';

// ============ Lucide 图标中文标签 ============
export const iconLabels: Record<string, string> = {
  menu: '菜单', x: '关闭', chevronLeft: '左箭头', chevronRight: '右箭头',
  chevronDown: '下箭头', chevronUp: '上箭头', arrowLeft: '向左', arrowRight: '向右',
  arrowUp: '向上', arrowDown: '向下', home: '首页', layout: '布局',
  grid: '网格', list: '列表', moreHorizontal: '更多', moreVertical: '更多',
  plus: '添加', minus: '减少', check: '确认', checkCircle: '确认圆圈',
  circle: '圆圈', trash: '删除', trash2: '删除2', edit: '编辑', pencil: '铅笔',
  copy: '复制', save: '保存', download: '下载', upload: '上传',
  search: '搜索', filter: '筛选', refresh: '刷新', undo: '撤销', redo: '重做',
  send: '发送', share: '分享', bookmark: '书签', flag: '标记', printer: '打印',
  link: '链接', externalLink: '外部链接', crop: '裁剪', move: '移动',
  lock: '锁定', unlock: '解锁', shield: '盾牌', shieldCheck: '盾牌确认',
  key: '密钥', eye: '眼睛', eyeOff: '眼睛关闭',
  settings: '设置', tool: '工具', wrench: '扳手', sliders: '滑块',
  toggleLeft: '左开关', toggleRight: '右开关', power: '电源',
  logIn: '登录', logOut: '登出',
  user: '用户', userPlus: '添加用户', userMinus: '减少用户',
  userCheck: '用户确认', userX: '用户删除', users: '多用户',
  group: '群组', idCard: '身份证',
  building: '建筑', briefcase: '公文包', store: '商店', cart: '购物车',
  shoppingCart: '购物车2', creditCard: '信用卡', wallet: '钱包',
  dollarSign: '美元', coin: '硬币', receipt: '收据',
  phone: '电话', mail: '邮件', message: '消息', messageCircle: '消息圈',
  bell: '铃铛',
  image: '图片', video: '视频', camera: '相机', mic: '麦克风',
  music: '音乐', play: '播放', pause: '暂停', volume: '音量',
  mapPin: '地图钉', map: '地图', clock: '时钟', calendar: '日历',
  timer: '计时器',
  file: '文件', fileText: '文本文件', folder: '文件夹', folderOpen: '打开文件夹',
  archive: '归档', book: '书籍', clipboard: '剪贴板',
  barChart2: '柱状图', barChart3: '柱状图3', lineChart: '折线图',
  pieChart: '饼图', trendingUp: '上涨', trendingDown: '下跌',
  alertCircle: '警告圈', alertTriangle: '警告三角', helpCircle: '帮助圈',
  info: '信息',
  star: '星星', heart: '心形', gift: '礼物', award: '奖项',
  medal: '奖牌', trophy: '奖杯', crown: '皇冠', sparkles: '闪光',
  rocket: '火箭', target: '目标', palette: '调色板', cloud: '云',
  sun: '太阳', moon: '月亮',
  facebook: 'Facebook', twitter: 'Twitter', github: 'GitHub',
  instagram: 'Instagram', youtube: 'YouTube', dribbble: 'Dribbble',
  figma: 'Figma',
  layers: '图层', type: '文字', barChart: '柱状图',
};

// ============ 默认值 ============
export const defaultDisplayFields: DisplayFields = {
  name: true, position: true, avatar: false, phone: true,
  email: true, company: true, companyAddress: true
};

/** 政务服务信息：独立对象，各项占位提示 */
export const GOV_SERVICE_PLACEHOLDERS = {
  serviceTime: '【示例】周一至周五 9:00-17:00（午间 12:00-13:30 不间断服务）',
  hotline: '【示例】12345 民生热线、XXX-XXXX-XXXX（应急值班）',
  trafficGuide: '【示例】公交：XX 路、XX 路至 XX 站下车；地铁 X 号线 XX 站 B 出口步行 500 米；自驾导航搜索“XX 街道办事处”，门口有停车位。',
  accountName: '【示例】XX 街道办事处',
  entryName: '【示例】政务服务网/粤省事小程序',
};

export const defaultContact: ContactInfo = {
  qrcode: '', address: '', phone: '', email: '',
};

export const createDefaultGovService = (): GovServiceInfo => ({
  serviceTime: '',
  serviceHotlines: [''],
  trafficGuides: [''],
  publicAccount: { name: '', qrcode: '' },
  onlineEntries: [{ name: '', image: '' }],
});

// ============ 单位简介（独立对象） ============
/** 机构类型预设选项（可勾选，也可手动输入） */
export const ORG_TYPE_OPTIONS = [
  '街道办事处', '党群服务中心', '社区居委会',
  '乡镇人民政府', '村民委员会', '开发区管委会',
  '政务服务中心', '社会组织', '其他',
];

/** 机构类型 / 单位定位 最多数量 */
export const MAX_UNIT_TYPES = 3;
export const MAX_UNIT_POSITIONING = 3;

/** 单位简介：各字段占位提示 */
export const UNIT_PROFILE_PLACEHOLDERS = {
  unitName: '【示例】XX 市 XX 区 XX 街道办事处',
  unitType: '【示例】街道办事处 / 党群服务中心 / 社区居委会',
  introduction: '【示例】XX 街道办事处位于 XX 区中部，辖区面积 X 平方公里，下辖 X 个社区，常住人口约 X 万人，主要承担基层治理、民生服务、党群工作等职能。',
  positioning: '【示例】基层治理执行者、民生服务提供者、党群工作组织者',
  jurisdictionOverview: '【示例】辖区面积 X 平方公里，常住人口 X 万人，下辖 X 个社区，主要地标有 XX 广场、XX 公园等。',
  departmentTitle: '【示例】党政综合办公室',
  departmentDescription: '【示例】综合协调、办文办会',
  departmentPhone: '【示例】XXX-XXXXXXX',
};

export const createDefaultUnitProfile = (): UnitProfile => ({
  unitName: '',
  unitTypes: [],
  introduction: '',
  unitPositioning: [],
  jurisdictionOverview: '',
  departments: [],
});

export const createDefaultDepartment = (): UnitDepartmentItem => ({
  id: Date.now().toString(),
  title: '',
  description: '',
  phone: '',
});

export const defaultCompanyIntro: CompanyIntro = {
  companyName: '',
  companyTypes: [],
  foundedDate: '',
  registeredCapital: '',
  creditRating: '',
  employeeCount: '',
  description: '',
  companyPositioning: [],
  businessSectors: []
};

export const defaultCompanyQualifications: CompanyQualifications = {
  honors: [],
  businessQualifications: [],
  strength: [],
  endorsement: []
};

export const defaultCooperation: Cooperation = {
  count: 0,
  logos: []
};

export const defaultCompanyPromo: CompanyPromo = {
  images: [],
  videos: []
};

// ============ 补充信息（选填） ============
/** 补充信息：各字段占位提示 */
export const SUPPLEMENT_INFO_PLACEHOLDERS = {
  websiteName: '【示例】XX 科技官网',
  websiteUrl: '【示例】www.xxcompany.com',
  socialMediaName: '【示例】微信公众号：XX 科技、抖音号：XX 科技官方、视频号：XX 科技',
  otherContent: '【示例】如有其他需要对外展示的信息，请在此填写',
};

export const createDefaultSocialMediaAccount = (): SocialMediaAccount => ({
  id: Date.now().toString() + Math.random().toString(36).slice(2, 6),
  image: '',
  name: '',
});

export const createDefaultSupplementInfo = (): SupplementInfo => ({
  officialWebsite: { name: '', url: '' },
  socialMediaAccounts: [],
  otherContent: '',
});

export const defaultCard: Omit<CardConfig, 'id' | 'createdAt'> = {
  name: '',
  logo: '',
  pageBg: '',
  mainTitle: '',
  subTitle: '',
  subTitleEn: '',
  themeColor: DEFAULT_THEME_COLOR,
  contact: { ...defaultContact },
  govService: createDefaultGovService(),
  unitIntro: createDefaultUnitProfile(),
  displayFields: { ...defaultDisplayFields },
  cardBgType: 'custom',
  cardBg: DEFAULT_BACKGROUND_LIST[0],
  cardLayout: 'left',
  status: 'active',
  saveToContacts: false,
  enablePoster: false,
  posterBg: '',
  posterCardPosition: { x: 50, y: 50 },
  posterInvitePosition: { x: 50, y: 50 },
  enableMiniCode: false,
  enableShortLink: false,
  selectedPages: [],
  philosophyItems: [],
  coreBusinessItems: [],
  productIntro: [],
  featureServices: [],
  companyIntro: { ...defaultCompanyIntro },
  companyQualifications: { ...defaultCompanyQualifications },
  investmentProjectItems: [],
  companyStrengthItems: [],
  cooperation: { ...defaultCooperation },
  companyPromo: { images: [], videos: [] },
  visionItems: [],
  promoVideo: '',
  supplementInfo: createDefaultSupplementInfo(),
  moduleType: '1',
};

// ============ 初始卡片数据 ============
export const initialCards: CardConfig[] = [
  {
    ...defaultCard,
    id: 1,
    name: '企业电子名片',
    logo: 'https://www.cdkmzs.com/uploads/platform/0703/capital_logo.png',
    pageBg: 'https://www.cdkmzs.com/uploads/platform/0703/e-cardBg.png',
    mainTitle: '匠造建材 · 链通供需',
    subTitle: '金属建材全产业链智造、数字服务与供应链综合服务商',
    subTitleEn: 'FULL INDUSTRY CHAIN INTEGRATED SERVICE FOR METAL BUILDING MATERIALS',
    contact: {
      qrcode: 'https://trae-api-cn.mchost.guru/api/ide/v1/text_to_image?prompt=qr%20code&image_size=square',
      address: '北京市朝阳区科技园区88号',
      phone: '400-888-8888',
      email: 'contact@company.com'
    },
    govService: createDefaultGovService(),
    displayFields: { name: true, position: true, avatar: false, phone: true, email: true, company: true, companyAddress: true },
    cardBgType: 'custom',
    cardBg: DEFAULT_BACKGROUND_LIST[0],
    cardLayout: 'left',
    status: 'active',
    createdAt: '2024-01-15',
    saveToContacts: false,
    enablePoster: false,
    posterBg: '',
    posterCardPosition: { x: 50, y: 50 },
    posterInvitePosition: { x: 50, y: 50 },
    enableMiniCode: false,
    enableShortLink: false,
    selectedPages: [],
    philosophyItems: [],
    coreBusinessItems: [],
    productIntro: [],
    featureServices: [],
    promoVideo: ''
  }
];
